import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  Spin,
  Space,
  Typography,
  Empty,
  Tooltip,
} from 'antd';
import { MessageOutlined, SendOutlined, PlusOutlined, DeleteOutlined, ShoppingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import ProductImagesFormField from '../../../components/productImagesForField/ProductImagesFormField';
import { linkifyText } from '../../../utils/linkify';
import CommentAttachments from '../../../components/CommentAttachments/CommentAttachments';
import CommentAttachmentInput from '../../../components/CommentAttachments/CommentAttachmentInput';
import '../../order/edit/OrderEdit.css';
import '../create/CreateBulkOrder.css';

const MODE_OF_PAYMENT_OPTIONS = [
  { value: 'PayPal', label: 'PayPal' },
  { value: 'Razorpay', label: 'Razorpay' },
  { value: 'PayGlocal', label: 'PayGlocal' },
  { value: 'Bank Transfer', label: 'Bank Transfer' },
  { value: 'Others', label: 'Others' },
];

const ORDER_DISPATCHED_OPTIONS = [
  { value: 'inprogress', label: 'In progress' },
  { value: 'half', label: 'Half' },
  { value: 'full', label: 'Full' },
];

const ORDER_STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'inprogress', label: 'In progress' },
  { value: 'ready', label: 'Ready' },
  { value: 'dispatch', label: 'Dispatch' },
];

/** Max lengths for text fields (characters) */
const LEN = {
  customerName: 100,
  concernedPerson: 100,
  bulkOrderNumber: 50,
  itemName: 150,
  itemDescription: 500,
  remark: 200,
};

const MAX_IMAGES_PER_ITEM = 5;

/** Max values for numeric line-item fields */
const MAX_QTY = 999999;
const MAX_PRICE = 999999999;

const MAX_LINE_ITEMS = 30;

const MARKING_OPTIONS = [
  { value: 'No', label: 'No' },
  { value: '14K', label: '14K' },
  { value: '18K', label: '18K' },
  { value: '10K', label: '10K' },
  { value: '9K', label: '9K' },
  { value: '925', label: '925' },
];

export default function BulkOrderEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [bulkOrder, setBulkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [commentLoading, setCommentLoading] = useState(false);
  const chatMessagesRef = useRef(null);
  const { user: currentUser } = useSelector((state) => state.authReducer);
  const isAdmin = currentUser?.roles?.some((r) => r.value === 'admin');

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`/bulk-orders/${id}`)
      .then((res) => {
        if (!cancelled) setBulkOrder(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = err.response?.data?.error || 'Failed to load bulk order';
          dispatch(setMessage({ message: msg, type: 'error' }));
          navigate('/bulk-orders');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, navigate, dispatch]);

  useEffect(() => {
    if (!bulkOrder) return;
    const orderList = Array.isArray(bulkOrder.orderList) ? bulkOrder.orderList : [];
    const parcelImages = Array.isArray(bulkOrder.parcelImages) ? bulkOrder.parcelImages : [];
    const marking = Array.isArray(bulkOrder.marking) ? bulkOrder.marking : [];
    form.setFieldsValue({
      customerName: bulkOrder.customerName ?? '',
      orderAmount: bulkOrder.orderAmount ?? 0,
      modeOfPayment: bulkOrder.modeOfPayment ?? undefined,
      concernedPerson: bulkOrder.concernedPerson ?? '',
      orderDate: bulkOrder.orderDate ? dayjs(bulkOrder.orderDate) : null,
      targetDate: bulkOrder.targetDate ? dayjs(bulkOrder.targetDate) : null,
      bulkOrderNumber: bulkOrder.bulkOrderNumber ?? '',
      orderDispatched: bulkOrder.orderDispatched ?? 'inprogress',
      orderList: orderList.length > 0 ? orderList.map((item) => ({
        orderStatus: item.orderStatus ?? 'open',
        photoOrFile: Array.isArray(item.photoOrFile) ? item.photoOrFile : [],
        itemName: item.itemName ?? '',
        itemDescription: item.itemDescription ?? '',
        quantity: item.quantity ?? 0,
        pricePerPiece: item.pricePerPiece ?? 0,
        deliveryDate: item.deliveryDate ? dayjs(item.deliveryDate) : null,
        remark: item.remark ?? '',
      })) : [{}],
      parcelImages,
      qualityGhatTheek: bulkOrder.qualityGhatTheek === true,
      qualityStoneSahi: bulkOrder.qualityStoneSahi === true,
      qualityOilLaga: bulkOrder.qualityOilLaga === true,
      qualitySettingTheek: bulkOrder.qualitySettingTheek === true,
      qualityNoScratch: bulkOrder.qualityNoScratch === true,
      qualityPolishTheek: bulkOrder.qualityPolishTheek === true,
      qualityRhodiumTheek: bulkOrder.qualityRhodiumTheek === true,
      qualityEarringPush: bulkOrder.qualityEarringPush === true,
      qualityLockBand: bulkOrder.qualityLockBand === true,
      ringSize: bulkOrder.ringSize ?? '',
      samaanGinti: bulkOrder.samaanGinti ?? '',
      marking,
    });
  }, [bulkOrder, form]);

  const handleAddComment = async () => {
    const trimmed = commentText?.trim();
    if (!trimmed) return;
    setCommentLoading(true);
    try {
      const res = await axios.post(`/bulk-orders/${id}/comments`, {
        text: trimmed,
        attachments: commentAttachments,
      });
      setBulkOrder(res.data);
      setCommentText('');
      setCommentAttachments([]);
      dispatch(setMessage({ message: 'Comment added', type: 'success' }));
      setTimeout(() => { if (chatMessagesRef.current) chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight; }, 100);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to add comment';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setCommentLoading(false);
    }
  };

  const buildPayload = (values) => {
    const orderList = (values.orderList || []).map((item) => ({
      orderStatus: ORDER_STATUS_OPTIONS.some((o) => o.value === item.orderStatus) ? item.orderStatus : 'open',
      photoOrFile: Array.isArray(item.photoOrFile) ? item.photoOrFile : [],
      itemName: item.itemName?.trim() ?? '',
      itemDescription: item.itemDescription?.trim() ?? '',
      quantity: Number(item.quantity) || 0,
      pricePerPiece: Number(item.pricePerPiece) || 0,
      deliveryDate: item.deliveryDate ? dayjs(item.deliveryDate).toISOString() : null,
      remark: item.remark?.trim() ?? '',
    }));
    return {
      customerName: values.customerName?.trim() ?? '',
      orderAmount: Number(values.orderAmount) ?? 0,
      modeOfPayment: values.modeOfPayment,
      concernedPerson: values.concernedPerson?.trim() ?? '',
      orderDate: values.orderDate ? dayjs(values.orderDate).toISOString() : null,
      targetDate: values.targetDate ? dayjs(values.targetDate).toISOString() : null,
      bulkOrderNumber: values.bulkOrderNumber?.trim() ?? '',
      orderList,
      orderDispatched: values.orderDispatched ?? 'inprogress',
      parcelImages: Array.isArray(values.parcelImages) ? values.parcelImages : [],
      qualityGhatTheek: values.qualityGhatTheek === true,
      qualityStoneSahi: values.qualityStoneSahi === true,
      qualityOilLaga: values.qualityOilLaga === true,
      qualitySettingTheek: values.qualitySettingTheek === true,
      qualityNoScratch: values.qualityNoScratch === true,
      qualityPolishTheek: values.qualityPolishTheek === true,
      qualityRhodiumTheek: values.qualityRhodiumTheek === true,
      qualityEarringPush: values.qualityEarringPush === true,
      qualityLockBand: values.qualityLockBand === true,
      ringSize: values.ringSize?.trim() ?? '',
      samaanGinti: values.samaanGinti?.trim() ?? '',
      marking: Array.isArray(values.marking) ? values.marking : [],
    };
  };

  const handleFinish = async (values) => {
    const rawList = values.orderList || [];
    if (rawList.length > MAX_LINE_ITEMS) {
      dispatch(setMessage({ message: `Maximum ${MAX_LINE_ITEMS} line items allowed`, type: 'warning' }));
      return;
    }
    setSubmitLoading(true);
    try {
      await axios.put(`/bulk-orders/${id}`, buildPayload(values));
      dispatch(setMessage({ message: 'Bulk order updated successfully', type: 'success' }));
      navigate('/bulk-orders');
    } catch (err) {
      const msg = err.response?.data?.error || 'Update failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="order-edit-page">
        <div className="order-edit-loading">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!bulkOrder) return null;

  const { Title } = Typography;

  return (
    <div className="order-edit-page">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div className="order-edit-header">
          <div className="order-edit-header-left">
            <Title level={3} style={{ margin: 0 }}>
              Edit bulk order
            </Title>
            {bulkOrder.bulkOrderNumber && (
              <p className="order-edit-subtitle">Bulk Order #{bulkOrder.bulkOrderNumber}</p>
            )}
          </div>
          <Space>
            <Button
              type="primary"
              size="large"
              loading={submitLoading}
              onClick={() => form.submit()}
              className="app-btn-primary"
            >
              Save changes
            </Button>
            <Button onClick={() => navigate('/bulk-orders')} size="large" className="app-btn-cancel">
              Cancel
            </Button>
          </Space>
        </div>

        <Form form={form} layout="vertical" onFinish={handleFinish} className="order-edit-form">
          <Card title="Order details" className="order-edit-card">
            <div className="order-edit-body">
              <Row gutter={16}>
                <Col xs={24} sm={24} md={8}>
<Form.Item
                  name="customerName"
                  label="Customer name"
                  rules={[
                    { required: true, message: 'Customer name is required' },
                    { max: LEN.customerName, message: `Customer name must be at most ${LEN.customerName} characters` },
                  ]}
                >
                  <Input placeholder="Customer name" maxLength={LEN.customerName} showCount />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="orderAmount" label="Order amount" rules={[{ required: true, message: 'Order amount is required' }]}>
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="0" onKeyDown={(e) => { if (!/[0-9.]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight','Home','End'].includes(e.key)) e.preventDefault(); }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="modeOfPayment" label="Mode of payment" rules={[{ required: true, message: 'Please select mode of payment' }]}>
                    <Select placeholder="Select" options={MODE_OF_PAYMENT_OPTIONS} allowClear={false} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
<Form.Item
                  name="concernedPerson"
                  label="Concerned person for order"
                  rules={[{ max: LEN.concernedPerson, message: `Concerned person must be at most ${LEN.concernedPerson} characters` }]}
                >
                  <Input placeholder="Concerned person" maxLength={LEN.concernedPerson} showCount />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
<Form.Item
                  name="bulkOrderNumber"
                  label="Bulk order number"
                  rules={[
                    { required: true, message: 'Bulk order number is required' },
                    { max: LEN.bulkOrderNumber, message: `Bulk order number must be at most ${LEN.bulkOrderNumber} characters` },
                  ]}
                >
                  <Input placeholder="Bulk order number" maxLength={LEN.bulkOrderNumber} showCount />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="orderDate" label="Order date" rules={[{ required: true, message: 'Order date is required' }]}>
                    <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} placeholder="Select date" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="targetDate" label="Target date">
                    <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} placeholder="Select date" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="orderDispatched" label="Order dispatched">
                    {isAdmin ? (
                      <Select placeholder="Select" options={ORDER_DISPATCHED_OPTIONS} />
                    ) : (
                      <Select placeholder="Select" options={ORDER_DISPATCHED_OPTIONS} disabled />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Card>

          <Card className="order-edit-card bulk-order-list-card" style={{ marginTop: 16 }}>
            <div className="bulk-order-list-header">
              <Typography.Title level={5} style={{ margin: 0 }}>
                Line items
              </Typography.Title>
              <Typography.Text type="secondary">
                Add or edit products or services for this bulk order
              </Typography.Text>
            </div>
            <Form.List name="orderList">
              {(fields, { add, remove }) => (
                <div className="bulk-order-list-body">
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div key={key} className="bulk-order-line-card">
                      <div className="bulk-order-line-card-header">
                        <Space align="center" size={12}>
                          <span className="bulk-order-line-badge">
                            <ShoppingOutlined className="bulk-order-line-badge-icon" />
                            Item {index + 1}
                          </span>
                          <Form.Item noStyle shouldUpdate={(prev, curr) => prev?.orderList?.[name]?.quantity !== curr?.orderList?.[name]?.quantity || prev?.orderList?.[name]?.pricePerPiece !== curr?.orderList?.[name]?.pricePerPiece}>
                          </Form.Item>
                        </Space>
                        <Tooltip title="Remove line item">
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                            className="bulk-order-line-remove"
                            aria-label="Remove line"
                          />
                        </Tooltip>
                      </div>
                      <div className="bulk-order-line-card-content">
                        <Row gutter={[16, 14]} className="bulk-order-line-fields">
                          <Col span={24} className="bulk-order-field-row">
                            <Form.Item
                              {...restField}
                              name={[name, 'photoOrFile']}
                              label="Photos"
                              className="bulk-order-field"
                              rules={[
                                {
                                  validator: (_, value) => {
                                    if (!value || !Array.isArray(value) || value.length <= MAX_IMAGES_PER_ITEM) return Promise.resolve();
                                    return Promise.reject(new Error(`Maximum ${MAX_IMAGES_PER_ITEM} images allowed per item`));
                                  },
                                },
                              ]}
                            >
                              <ProductImagesFormField addButtonText="Add photo" hideHint className="bulk-order-upload" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={8} md={8} className="bulk-order-field-row">
                            <Form.Item
                              {...restField}
                              name={[name, 'itemName']}
                              label="Item name"
                              className="bulk-order-field"
                              rules={[
                                { required: true, message: 'Item name is required' },
                                { max: LEN.itemName, message: `Item name must be at most ${LEN.itemName} characters` },
                              ]}
                            >
                              <Input placeholder="Item name" maxLength={LEN.itemName} showCount />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={4} md={3} className="bulk-order-field-row">
                            <Form.Item
                              {...restField}
                              name={[name, 'quantity']}
                              label="Qty"
                              className="bulk-order-field"
                              rules={[
                                { required: true, message: 'Qty is required' },
                                { type: 'number', min: 0, message: 'Qty must be at least 0' },
                                { type: 'number', max: MAX_QTY, message: `Qty must be at most ${MAX_QTY.toLocaleString()}` },
                              ]}
                            >
                              <InputNumber min={0} max={MAX_QTY} placeholder="0" style={{ width: '100%' }} onKeyDown={(e) => { if (!/[0-9.]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight','Home','End'].includes(e.key)) e.preventDefault(); }} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={4} md={3} className="bulk-order-field-row">
                            <Form.Item
                              {...restField}
                              name={[name, 'pricePerPiece']}
                              label="Price"
                              className="bulk-order-field"
                              rules={[
                                { required: true, message: 'Price is required' },
                                { type: 'number', min: 0, message: 'Price must be at least 0' },
                                { type: 'number', max: MAX_PRICE, message: `Price must be at most ${MAX_PRICE.toLocaleString()}` },
                              ]}
                            >
                              <InputNumber min={0} max={MAX_PRICE} placeholder="0" style={{ width: '100%' }} onKeyDown={(e) => { if (!/[0-9.]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight','Home','End'].includes(e.key)) e.preventDefault(); }} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={4} md={6} className="bulk-order-field-row">
                            <Form.Item {...restField} name={[name, 'deliveryDate']} label="Delivery date" className="bulk-order-field">
                              <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} placeholder="Select" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={4} md={4} className="bulk-order-field-row">
                            <Form.Item {...restField} name={[name, 'orderStatus']} label="Status" initialValue="open" className="bulk-order-field">
                              <Select placeholder="Status" options={ORDER_STATUS_OPTIONS} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12} className="bulk-order-field-row bulk-order-desc-remark-start">
                            <Form.Item {...restField} name={[name, 'itemDescription']} label="Description" className="bulk-order-field" rules={[{ max: LEN.itemDescription, message: `Description must be at most ${LEN.itemDescription} characters` }]}>
                              <Input.TextArea rows={2} placeholder="Short description" maxLength={LEN.itemDescription} showCount />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12} className="bulk-order-field-row bulk-order-desc-remark-start">
                            <Form.Item {...restField} name={[name, 'remark']} label="Remark" className="bulk-order-field" rules={[{ max: LEN.remark, message: `Remark must be at most ${LEN.remark} characters` }]}>
                              <Input.TextArea rows={2} placeholder="Optional note" maxLength={LEN.remark} showCount />
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => fields.length < MAX_LINE_ITEMS && add()}
                    block
                    icon={<PlusOutlined />}
                    className="bulk-order-add-btn"
                    size="large"
                    disabled={fields.length >= MAX_LINE_ITEMS}
                  >
                    {fields.length >= MAX_LINE_ITEMS ? `Maximum ${MAX_LINE_ITEMS} line items reached` : 'Add line item'}
                  </Button>
                </div>
              )}
            </Form.List>
          </Card>

          <Card title="Quality check" className="order-edit-card order-edit-quality-card">
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <div className="order-edit-quality-checkboxes">
                  <Form.Item name="qualityGhatTheek" valuePropName="checked" style={{ marginBottom: 12 }}>
                    <Checkbox>घाट ठीक है</Checkbox>
                  </Form.Item>
                  <Form.Item name="qualityStoneSahi" valuePropName="checked" style={{ marginBottom: 12 }}>
                    <Checkbox>स्टोन सही है</Checkbox>
                  </Form.Item>
                  <Form.Item name="qualityOilLaga" valuePropName="checked" style={{ marginBottom: 12 }}>
                    <Checkbox>ऑयल लगा दिया</Checkbox>
                  </Form.Item>
                  <Form.Item name="qualitySettingTheek" valuePropName="checked" style={{ marginBottom: 12 }}>
                    <Checkbox>सेटिंग ठीक है</Checkbox>
                  </Form.Item>
                  <Form.Item name="qualityNoScratch" valuePropName="checked" style={{ marginBottom: 12 }}>
                    <Checkbox>कोई स्क्रैच नहीं है</Checkbox>
                  </Form.Item>
                  <Form.Item name="qualityPolishTheek" valuePropName="checked" style={{ marginBottom: 12 }}>
                    <Checkbox>पॉलिश ठीक है</Checkbox>
                  </Form.Item>
                  <Form.Item name="qualityRhodiumTheek" valuePropName="checked" style={{ marginBottom: 12 }}>
                    <Checkbox>रोडियम ठीक है</Checkbox>
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div className="order-edit-quality-checkboxes">
                  <Form.Item name="qualityEarringPush" valuePropName="checked" style={{ marginBottom: 12 }}>
                    <Checkbox>इयररिंग का पुश सही काम कर रहा है</Checkbox>
                  </Form.Item>
                  <Form.Item name="qualityLockBand" valuePropName="checked" style={{ marginBottom: 12 }}>
                    <Checkbox>लॉक सही से बंद हो रहा है</Checkbox>
                  </Form.Item>
                </div>
                <div style={{ marginTop: 16 }}>
                  <Form.Item name="ringSize" label="रिंग साइज">
                    <Input placeholder="रिंग साइज" />
                  </Form.Item>
                  <Form.Item name="samaanGinti" label="समान की गिनती">
                    <Input placeholder="समान की गिनती" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24}>
                <Form.Item name="marking" label="मार्किंग" style={{ marginBottom: 0, marginTop: 8 }}>
                  <Checkbox.Group
                    options={MARKING_OPTIONS}
                    className="order-edit-marking-checkboxes"
                    style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 24px' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="parcelImages" label="पार्सल तस्वीर">
                  <ProductImagesFormField
                    addButtonText="तस्वीर जोड़ें"
                    hintText="एक या अधिक तस्वीरें चुनें (अधिकतम ५)।"
                    className="order-edit-upload-large"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Form>

        <Card title={<span><MessageOutlined style={{ marginRight: 8 }} />Comments</span>} className="order-edit-comments-card">
          <div className="order-edit-chat">
            <div className="order-edit-chat-messages" ref={chatMessagesRef}>
              {bulkOrder.comments && bulkOrder.comments.length > 0 ? (
                bulkOrder.comments.map((item, idx) => {
                  const isSystem = !item.created_by;
                  const name = item.created_by ? (item.created_by.name || item.created_by.email || '—') : 'Kosha';
                  const initial = (name !== '—' && name !== 'Kosha' ? name.charAt(0) : isSystem ? '' : name.charAt(0)).toUpperCase();
                  const isOwn = currentUser?.email && item.created_by?.email === currentUser.email;
                  return (
                    <div
                      key={item._id || `${item.created_at}-${idx}`}
                      className={`order-edit-chat-bubble-wrap ${isOwn ? 'order-edit-chat-bubble-own' : ''}`}
                    >
                      {!isOwn && (
                        <div className={`order-edit-chat-avatar ${isSystem ? 'order-edit-chat-avatar-kosha' : ''}`}>
                          {isSystem ? (
                            <img src={`${process.env.PUBLIC_URL || ''}/KOSHA-logo.png`} alt="Kosha" />
                          ) : (
                            initial
                          )}
                        </div>
                      )}
                      <div className="order-edit-chat-bubble">
                        <div className="order-edit-chat-bubble-header">
                          <span className="order-edit-chat-name">{isOwn ? 'You' : name}</span>
                          <span className="order-edit-chat-time">
                            {dayjs(item.created_at).isSame(dayjs(), 'day')
                              ? `Today ${dayjs(item.created_at).format('h:mm A')}`
                              : dayjs(item.created_at).format('MMM D, YYYY h:mm A')}
                          </span>
                        </div>
                        <div className="order-edit-chat-bubble-text" style={{ whiteSpace: 'pre-line' }}>{linkifyText(item.text)}</div>
                        {item.attachments && item.attachments.length > 0 && (
                          <CommentAttachments attachments={item.attachments} className="order-edit-chat-bubble-attachments" />
                        )}
                      </div>
                      {isOwn && <div className="order-edit-chat-avatar order-edit-chat-avatar-own">{initial}</div>}
                    </div>
                  );
                })
              ) : (
                <Empty description="No comments yet" image={Empty.PRESENTED_IMAGE_SIMPLE} className="order-edit-chat-empty" />
              )}
            </div>
            <div className="order-edit-comments-add">
              <Input.TextArea
                rows={2}
                placeholder="Type a message..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                className="order-edit-comment-input"
                autoSize={{ minRows: 2, maxRows: 4 }}
                maxLength={500}
              />
              <div className="order-edit-comment-footer">
                <CommentAttachmentInput
                  value={commentAttachments}
                  onChange={setCommentAttachments}
                  disabled={commentLoading}
                />
                <span className="order-edit-comment-count">{commentText.length}/500 characters</span>
                <Button
                  type="text"
                  shape="circle"
                  icon={<SendOutlined className="order-edit-comment-icon" />}
                  onClick={handleAddComment}
                  loading={commentLoading}
                  disabled={!commentText?.trim()}
                  className="order-edit-comment-add-btn"
                  aria-label="Add comment"
                />
              </div>
            </div>
          </div>
        </Card>
      </Space>
    </div>
  );
}
